// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

import { useCallback, useEffect, useState } from 'react'
import * as api from '../../api/client'
import ManufacturerDrugPicker, { type DrugGrant } from './ManufacturerDrugPicker'

/**
 * D-MFR-1 — the whole manufacturer access panel: drug grant, PHI setting, agreement
 * reference, save.
 *
 * This is a COMPONENT and not page code on purpose. There are two user-management
 * surfaces in this app — UsersTab (routed at /manage-users and the admin users tab)
 * and ManageUsersPage — and the Manufacturer role can be assigned from either. The
 * first attempt at this shared only the drug picker and hand-rolled the surrounding
 * panel into one page, which meant the other surface silently had no way to set a
 * grant at all: the role went on, the portal fell back to showing nothing, and there
 * was no control anywhere to fix it. Sharing the picker was not enough; the panel has
 * to be the shared unit.
 *
 * Mirrors PrescriberAccessSection's props exactly so it drops into the same slots.
 */
export default function ManufacturerAccessSection({ userId, roles, canWrite, onChanged }: {
  userId: string
  roles: { id: string; name: string }[]
  canWrite: boolean
  onChanged: () => void
}) {
  // Match on role NAME, not the permission-derived legacy role: the derived role only
  // refreshes on the user's next session, so it would lag a role assigned seconds ago,
  // and this panel exists precisely to react to what the admin just ticked.
  const show = roles.some(r => r.name === 'Manufacturer')

  const [grants, setGrants] = useState<DrugGrant[]>([])
  const [phiAccess, setPhiAccess] = useState<'masked' | 'full'>('masked')
  const [agreementRef, setAgreementRef] = useState('')
  const [phiConfirm, setPhiConfirm] = useState('')
  const [dirty, setDirty] = useState(false)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [note, setNote] = useState<string | null>(null)

  const load = useCallback(async () => {
    try {
      const d = await api.getUserDrugAccess(userId)
      setGrants(d.grants as DrugGrant[])
      setPhiAccess(d.phi_access)
      setAgreementRef(d.agreement_ref ?? '')
      setPhiConfirm(''); setDirty(false)
    } catch {
      // No grant record is a normal state for an account that has just been given the
      // role, and for every account that never had it. Not an error.
      setGrants([]); setPhiAccess('masked'); setAgreementRef(''); setDirty(false)
    }
  }, [userId])

  useEffect(() => { if (show) load() }, [show, load])

  if (!show) return null

  const confirmOk = phiAccess !== 'full' || phiConfirm.trim().toUpperCase() === 'ALLOW PHI'

  return (
    <div style={{ marginTop: 16, paddingTop: 14, borderTop: '1px solid var(--color-border)' }}>
      <h4 style={{ fontSize: 13, fontWeight: 600, margin: '0 0 4px' }}>Drugs this manufacturer can see</h4>
      <div style={{ fontSize: 12, color: 'var(--color-text-mute)', marginBottom: 10 }}>
        Search by NDC, drug name, brand, or manufacturer. Selecting a manufacturer heading
        grants every drug under it.
      </div>

      {err && (
        <div role="alert" style={{ fontSize: 12.5, color: '#991b1b', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, padding: '7px 10px', marginBottom: 8 }}>{err}</div>
      )}
      {note && (
        <div style={{ fontSize: 12.5, color: '#166534', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 6, padding: '7px 10px', marginBottom: 8 }}>{note}</div>
      )}

      {grants.length === 0 && (
        <div style={{ fontSize: 12.5, color: 'var(--color-warning, #ca8a04)', marginBottom: 8 }}>
          <em>No drugs granted — this account can sign in but sees nothing.</em>
        </div>
      )}

      <ManufacturerDrugPicker
        value={grants}
        disabled={busy || !canWrite}
        onChange={g => { setGrants(g); setDirty(true) }}
      />

      <div style={{ marginTop: 14, border: '1px solid var(--color-border)', borderRadius: 8, overflow: 'hidden' }}>
        <div style={{ padding: '10px 12px', borderBottom: '1px solid var(--color-border)', background: '#fcfcfb' }}>
          <div style={{ fontWeight: 600, fontSize: 13 }}>Patient information</div>
          <div style={{ fontSize: 12, color: 'var(--color-text-mute)', marginTop: 2 }}>
            What this account sees on every screen and export.
          </div>
        </div>

        <div style={{ display: 'flex', gap: 10, padding: 12, flexWrap: 'wrap' }}>
          {(['masked', 'full'] as const).map(mode => (
            <label key={mode} style={{
              flex: 1, minWidth: 200, display: 'flex', gap: 10, alignItems: 'flex-start', padding: 11,
              border: `1.5px solid ${phiAccess === mode ? (mode === 'full' ? 'var(--color-danger, #b91c1c)' : 'var(--color-primary, #0f766e)') : 'var(--color-border)'}`,
              borderRadius: 8, cursor: canWrite ? 'pointer' : 'default',
              background: phiAccess === mode ? (mode === 'full' ? '#fef2f2' : '#f0fdfa') : '#fff',
            }}>
              <input type="radio" name={`phi-${userId}`} checked={phiAccess === mode} disabled={busy || !canWrite}
                onChange={() => { setPhiAccess(mode); setPhiConfirm(''); setDirty(true) }} style={{ marginTop: 2 }} />
              <span>
                <span style={{ fontWeight: 600, fontSize: 13, display: 'block' }}>
                  {mode === 'masked' ? 'Mask patient information' : 'Allow patient information'}
                </span>
                <span style={{ fontSize: 12, color: 'var(--color-text-mute)', display: 'block', marginTop: 2, lineHeight: 1.45 }}>
                  {mode === 'masked'
                    ? 'Name as initials, record number as last 4, date of birth as year only.'
                    : 'Full name, record number and date of birth. Use only where your agreement with this recipient covers identified patient data.'}
                </span>
              </span>
            </label>
          ))}
        </div>

        {phiAccess === 'full' && (
          <div style={{ padding: 12, background: '#fef2f2', borderTop: '1px solid #fecaca', color: '#991b1b', fontSize: 12.5, lineHeight: 1.5 }}>
            <b style={{ display: 'block', marginBottom: 2 }}>This releases identified patient data to an external recipient.</b>
            Your pharmacy is the disclosing party. The change is logged with your name.
            <div style={{ marginTop: 9, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <span>Type <code style={{ fontFamily: 'ui-monospace, monospace' }}>ALLOW PHI</code> to confirm</span>
              <input type="text" value={phiConfirm} onChange={e => setPhiConfirm(e.target.value)}
                placeholder="ALLOW PHI" autoComplete="off" spellCheck={false}
                style={{ width: 170, fontFamily: 'ui-monospace, monospace', fontSize: 12.5, padding: '5px 9px', border: '1px solid #fecaca', borderRadius: 6 }} />
            </div>
          </div>
        )}

        <div style={{ padding: 12, borderTop: '1px solid var(--color-border)' }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-text-soft, #57534e)', display: 'block', marginBottom: 4 }}>
            Data-sharing agreement reference
          </label>
          <input type="text" value={agreementRef} disabled={busy || !canWrite}
            onChange={e => { setAgreementRef(e.target.value); setDirty(true) }}
            placeholder="e.g. LDD Data Reporting Exhibit C, 2026-03-01"
            style={{ width: '100%', padding: '7px 10px', border: '1px solid var(--color-border)', borderRadius: 6, fontSize: 13 }} />
          <div style={{ fontSize: 12, color: 'var(--color-text-mute)', marginTop: 4 }}>
            Recorded, not verified. Required before patient information can be allowed.
          </div>
        </div>
      </div>

      <div style={{ marginTop: 10, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <button
          disabled={busy || !canWrite || !dirty || !confirmOk}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 12px', borderRadius: 8,
            border: '1px solid var(--color-primary, #0f766e)', background: 'var(--color-primary, #0f766e)',
            color: '#fff', fontSize: 13, fontWeight: 600,
            cursor: (busy || !canWrite || !dirty || !confirmOk) ? 'default' : 'pointer',
            opacity: (busy || !canWrite || !dirty || !confirmOk) ? 0.5 : 1,
          }}
          onClick={async () => {
            setBusy(true); setErr(null); setNote(null)
            try {
              await api.setUserDrugAccess(userId, {
                grants,
                phi_access: phiAccess,
                agreement_ref: agreementRef.trim() || null,
              })
              setNote('Drug access saved.')
              await load(); onChanged()
            } catch (e: any) {
              setErr(e?.body?.message || e?.message || 'Could not save drug access.')
            } finally { setBusy(false) }
          }}>
          Save drug access
        </button>
        {dirty && <span style={{ fontSize: 12, color: 'var(--color-text-mute)' }}>Unsaved changes</span>}
      </div>
    </div>
  )
}
